<template>
    <div class="shrink-0 min-h-80 relative flex flex-col justify-center" :class="[heroClasses]">
        <div class="px-safe grow flex flex-col justify-center">
            <div class="px-4 w-full max-w-[36rem] mx-auto flex flex-col gap-4 relative items-center h-full justify-center">
                <div class="pt-12 p-8 flex items-center justify-center flex-col gap-6">
                    <div :class="emoticonClasses">
                        <Emoticon :type="alertState" class="w-32 h-32 border-4 border-system-light shadow"></Emoticon>
                    </div>
                    <div class="flex flex-col items-center gap-1">
                        <template v-if="labelStore?.labelData?.labelReference">
                            <div class="text-xl font-medium">{{ labelStore?.labelData?.labelReference }}</div>
                            <div class="text-sub">{{ labelStore?.labelData?.label?.sequenceCode ?? labelStore?.labelData?.label?.uid }}</div>
                        </template>
                        <template v-else>
                            <div class="text-xl font-medium">{{ labelStore?.labelData?.label?.sequenceCode ?? labelStore?.labelData?.label?.uid }}</div>
                            <div v-if="!labelStore.labelInDatabase" class="text-sub">Label report not found</div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <div class="max-w-[36rem] mx-auto w-full min-[36rem]:[mask-image:linear-gradient(to_right,_transparent,_black_8px,_black_calc(100%_-_8px),_transparent_100%)]">
            <SummaryCardCarousel v-if="calculatedSenseData && labelInNewDatabase && hasData">
                <SummaryCard
                    v-if="calculatedSenseData?.durationOfViolations?.temperature > 0"
                    :unit="UnitTypes.Temperature"
                    :label="toDaysHoursMinutesSeconds(calculatedSenseData?.durationOfViolations.temperature)"
                    state="error"
                    :badgeLabel="String(violationPercentages.temperature)"
                    badgeSuffix="%"
                />
                <SummaryCard v-else :unit="UnitTypes.Temperature" label="No violations" state="success" :disabled="true" />
                <template v-if="labelType === LabelType.HumiditySense">
                    <SummaryCard
                        v-if="calculatedSenseData?.durationOfViolations.humidity > 0"
                        :unit="UnitTypes.Humidity"
                        :label="toDaysHoursMinutesSeconds(calculatedSenseData?.durationOfViolations?.humidity)"
                        state="error"
                        :badgeLabel="String(violationPercentages.humidity)"
                        badgeSuffix="%"
                    />
                    <SummaryCard v-else :unit="UnitTypes.Humidity" label="No violations" state="success" :disabled="true" />
                </template>

                <template v-if="isLabelTap">
                    <SummaryCard v-if="labelHasStopped" :unit="UnitTypes.Label" :label="statusStringShort" type="status" />
                    <SummaryCard v-if="calculatedSenseData?.batteryStatus" :unit="UnitTypes.Battery" :label="calculatedSenseData?.batteryStatus" type="status" />
                    <SummaryCard v-else :unit="UnitTypes.Battery" :label="labelStore.batteryStatusString" type="status" />
                </template>
            </SummaryCardCarousel>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { LabelType, UnitTypes } from '@/types/enums'
    import { useLabelStore } from '@/stores/label'
    import { toDaysHoursMinutesSeconds } from '@/utils/time'

    import { Emoticon, Chip, UnitBadge, SummaryCardCarousel, SummaryCard } from '@tapp.online/ui'

    type EmoticonType = 'neutral' | 'success' | 'warning' | 'error'
    type AlertLevel = '-1' | 0 | 1 | 2

    const labelStore = useLabelStore()
    const isLabelTap = computed(() => labelStore.isLabelTap)
    const labelInDatabase = computed(() => labelStore.labelInDatabase)
    const labelInNewDatabase = computed(() => labelStore.labelHasReadAccessToken)
    const calculatedSenseData = computed(() => labelStore.labelData?.calculatedSenseData)
    const labelHasStopped = computed(() => labelStore.labelHasStopped)
    const statusStringShort = computed(() => labelStore.statusStringShort)
    const labelType = computed(() => labelStore.labelData?.type)

    const hasData = computed(() => {
        return labelStore.labelData?.calculatedSenseData?.datapoints?.length > 0
    })

    const violationPercentages = computed(() => {
        return {
            temperature: Math.round((calculatedSenseData.value?.numberOfViolations.temperature / calculatedSenseData.value?.numberOfMeasurementsOccurred) * 100),
            humidity: Math.round((calculatedSenseData.value?.numberOfViolations.humidity / calculatedSenseData.value?.numberOfMeasurementsOccurred) * 100)
        }
    })

    const alertLevel = computed<AlertLevel>(() => {
        if (!hasData.value) return '-1'
        if (labelStore.labelData?.alert) return labelStore.labelData?.alert?.level
        return labelStore.labelHasViolations ? 1 : 0
    })

    const heroClasses = computed(() => {
        const baseClasses = ['bg-gradient-to-b']

        if (alertLevel.value === 0) baseClasses.push('from-success-200 to-success-100')
        else if (alertLevel.value === 1) baseClasses.push('from-warning-200 to-warning-100')
        else if (alertLevel.value === 2) baseClasses.push('from-error-200 to-error-100')
        else baseClasses.push('from-neutral-100 to-neutral-100')
        return baseClasses
    })

    const emoticonClasses = computed(() => {
        const baseClasses = 'rounded-full p-3'
        const alertStateOptions = {
            success: 'bg-success-400',
            warning: 'bg-warning-300',
            error: 'bg-error-300',
            neutral: 'bg-neutral-200'
        }
        return [baseClasses, alertStateOptions[alertState.value]]
    })

    const alertState = computed<EmoticonType>(() => {
        const typeOptions = {
            '-1': 'neutral',
            '0': 'success',
            '1': 'warning',
            '2': 'error'
        }
        return typeOptions[alertLevel.value] as EmoticonType
    })
</script>
